import { weaponDefs, getWeapon } from './definitions.js';
import { triggerShoot } from '../entities/player_anim.js';
import { addCameraShake } from '../engine/camera.js';

let firingState = null;

export function initFiring(options = {}) {
  firingState = {
    weaponIndex: options.weaponIndex ?? 0,
    unlockedWeapons: new Set(options.unlockedWeapons || [0]),
    spawnBullet: options.spawnBullet,
    onMuzzleFlash: options.onMuzzleFlash || null,
    onWeaponChanged: options.onWeaponChanged || null,
    onWeaponUnlocked: options.onWeaponUnlocked || null,
    onFired: options.onFired || null,
  };
  return firingState;
}

export function currentWeapon() {
  if (!firingState) return weaponDefs[0];
  if (!firingState.unlockedWeapons.has(firingState.weaponIndex)) {
    firingState.weaponIndex = 0;
  }
  return getWeapon(firingState.weaponIndex);
}

export function selectWeapon(index) {
  if (!firingState || !weaponDefs[index]) return false;
  if (!firingState.unlockedWeapons.has(index)) return false;

  firingState.weaponIndex = index;
  firingState.onWeaponChanged?.(weaponDefs[index], index);
  return true;
}

export function getWeaponSlots() {
  const selected = firingState?.weaponIndex || 0;
  const unlocked = firingState?.unlockedWeapons || new Set([0]);
  return weaponDefs.map((weapon, index) => ({
    ...weapon,
    index,
    selected: index === selected,
    unlocked: unlocked.has(index),
    dps: weapon.damage * weapon.pellets / Math.max(0.01, weapon.cooldown),
  }));
}

export function cycleWeapon(direction = 1) {
  if (!firingState) return false;
  const slots = getWeaponSlots();
  const start = firingState.weaponIndex;
  for (let step = 1; step <= slots.length; step += 1) {
    const next = (start + step * Math.sign(direction || 1) + slots.length) % slots.length;
    if (selectWeapon(next)) return true;
  }
  return false;
}

export function unlockWeapon(index) {
  if (!firingState || !weaponDefs[index]) return false;
  const wasLocked = !firingState.unlockedWeapons.has(index);
  firingState.unlockedWeapons.add(index);
  if (wasLocked) firingState.onWeaponUnlocked?.(weaponDefs[index], index);
  return true;
}

export function fireWeapon(player, game = {}) {
  if (!firingState?.spawnBullet || !player) return false;
  if (player.shotCooldown > 0) return false;

  const weapon = currentWeapon();
  const upgrade = game.upgrades || {};
  const damageBoost = 1 + (upgrade.damage || 0);
  const fireRateBoost = 1 + (upgrade.fireRate || 0);
  const sizeBoost = 1 + (upgrade.bulletSize || 0);
  const boostedWeapon = {
    ...weapon,
    pellets: Math.min(13, weapon.pellets + Math.min(5, upgrade.pellets || 0)),
    damage: weapon.damage * damageBoost,
    pierce: (weapon.pierce || 0) + (upgrade.pierce || 0),
    radius: weapon.radius * sizeBoost,
    spread: game.ads ? weapon.spread * 0.5 : weapon.spread,
    speed: weapon.speed * (1 + (upgrade.bulletSpeed || 0)),
    ricochet: upgrade.ricochet || 0,
    explode: weapon.explode || upgrade.explode || 0,
    freeze: weapon.freeze || upgrade.freeze || 0,
    critChance: upgrade.critChance || 0,
    critMultiplier: 2.4 + (upgrade.critDamage || 0),
    stagger: upgrade.stagger || 0,
    execute: upgrade.execute || 0,
  };

  const bullets = firingState.spawnBullet(player.pos, player.aim, {
    weapon: boostedWeapon,
    overdrive: game.overdriveTime > 0,
  });
  if (!bullets.length) return false;

  player.shotCooldown = weapon.cooldown / fireRateBoost * (game.overdriveTime > 0 ? 0.54 : 1);
  triggerShoot(weapon.id === 'shotgun' ? 1.35 : weapon.id === 'rail' ? 1.6 : 1);
  addCameraShake(Math.min(0.24, (weapon.shake || 0.06) * 1.15), game.shakeEnabled);
  firingState.onMuzzleFlash?.(player, boostedWeapon);
  firingState.onFired?.(boostedWeapon, bullets);
  return true;
}

export function updateFiring(dt, player, game = {}) {
  if (!player) return;
  player.shotCooldown -= dt * (game.overdriveTime > 0 ? 1.38 : 1);
}
